// Admin-Panel › Übersicht: Kennzahlen, Speicher nach Kategorien/Präfixen, Top-Nutzer, Brücken-Kurzinfo.
import type { AdminStatsDto } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { adminApi, type AdminStorageDto } from '../api';
import { displayName, formatBytes, formatDateTime, formatNumber } from '../format';
import { Badge, Empty, Loading } from '../ui';
import { AdminError, AdminSection } from './parts';
import { ADMIN_STATS_KEY, ADMIN_STORAGE_KEY } from './realtime';

const BRIDGE_STATE_LABELS: Record<AdminStatsDto['bridge']['state'], { label: string; tone: 'neutral' | 'accent' | 'ok' | 'danger' }> = {
  idle: { label: 'Leerlauf', tone: 'neutral' },
  connecting: { label: 'Verbindet', tone: 'accent' },
  ready: { label: 'Verbunden', tone: 'ok' },
  error: { label: 'Fehler', tone: 'danger' },
};

const STORAGE_CATEGORIES: { key: keyof Omit<AdminStatsDto['storage'], 'totalBytes'>; label: string }[] = [
  { key: 'attachments', label: 'Anhänge' },
  { key: 'avatars', label: 'Avatare & Banner' },
  { key: 'emojis', label: 'Emojis & Sticker' },
  { key: 'exports', label: 'Exporte' },
];

function StatTile(props: { label: string; value: string; testId: string }) {
  return (
    <div className="lfx-card lfx-admin-stat" data-testid={props.testId}>
      <div className="lfx-admin-stat__value">{props.value}</div>
      <div className="lfx-muted">{props.label}</div>
    </div>
  );
}

function ShareBar(props: { bytes: number; total: number; testId: string; label: string }) {
  const pct = props.total > 0 ? Math.round((props.bytes / props.total) * 1000) / 10 : 0;
  return (
    <div className="lfx-admin-share" data-testid={props.testId}>
      <div className="lfx-admin-share__track" role="img" aria-label={`${props.label}: ${String(pct).replace('.', ',')} %`}>
        <div className="lfx-admin-share__bar" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="lfx-muted lfx-admin-share__pct">{String(pct).replace('.', ',')} %</span>
    </div>
  );
}

function StorageDetails(props: { storage: AdminStorageDto }) {
  const s = props.storage;
  const prefixes = Object.entries(s.byPrefix).sort((a, b) => b[1].bytes - a[1].bytes);
  return (
    <>
      <AdminSection title={`Speicher nach Bereich (gesamt ${formatBytes(s.totalBytes)})`} testId="admin-dash-storage">
        {prefixes.length === 0 ? (
          <Empty testId="admin-dash-storage-empty">Noch keine Dateien gespeichert.</Empty>
        ) : (
          <div className="lfx-table-wrap">
            <table className="lfx-table">
              <thead>
                <tr>
                  <th scope="col">Bereich</th>
                  <th scope="col">Dateien</th>
                  <th scope="col">Größe</th>
                  <th scope="col">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {prefixes.map(([prefix, v]) => (
                  <tr key={prefix} data-testid={`admin-dash-prefix-${prefix}`}>
                    <td className="lfx-mono">{prefix}</td>
                    <td>{formatNumber(v.count)}</td>
                    <td>{formatBytes(v.bytes)}</td>
                    <td>
                      <ShareBar bytes={v.bytes} total={s.totalBytes} testId={`admin-dash-prefix-share-${prefix}`} label={prefix} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminSection>
      <AdminSection title="Größte Speicher-Nutzer" testId="admin-dash-top-users">
        {s.topUsers.length === 0 ? (
          <Empty>Keine Daten.</Empty>
        ) : (
          <ol className="lfx-admin-list">
            {s.topUsers.map((t) => (
              <li key={t.user.id} className="lfx-row lfx-row--between" data-testid={`admin-dash-top-user-${t.user.id}`}>
                <Link to={`users/${t.user.id}`} className="lfx-link" data-testid={`admin-dash-top-user-link-${t.user.id}`}>
                  {displayName(t.user)} <span className="lfx-muted">@{t.user.username}</span>
                </Link>
                <span>{formatBytes(t.bytes)}</span>
              </li>
            ))}
          </ol>
        )}
      </AdminSection>
    </>
  );
}

export function DashboardPage() {
  const stats = useQuery({ queryKey: ADMIN_STATS_KEY, queryFn: adminApi.stats });
  const storage = useQuery({ queryKey: ADMIN_STORAGE_KEY, queryFn: adminApi.storage });
  const s = stats.data;
  const bridgeState = s ? BRIDGE_STATE_LABELS[s.bridge.state] : null;
  return (
    <div className="lfx-stack" data-testid="admin-dashboard">
      {stats.isLoading && <Loading testId="admin-dash-loading" />}
      <AdminError error={stats.error} testId="admin-dash-error" />
      {s && (
        <>
          <div className="lfx-admin-stats">
            <StatTile label="Nutzer" value={formatNumber(s.users)} testId="admin-dash-users" />
            <StatTile label="Online" value={formatNumber(s.onlineUsers)} testId="admin-dash-online" />
            <StatTile label="Server" value={formatNumber(s.servers)} testId="admin-dash-servers" />
            <StatTile label="Nachrichten" value={formatNumber(s.messages)} testId="admin-dash-messages" />
            <StatTile label="Datenbank" value={formatBytes(s.database.sizeBytes)} testId="admin-dash-db" />
            <StatTile label="Speicher gesamt" value={formatBytes(s.storage.totalBytes)} testId="admin-dash-storage-total" />
          </div>
          <AdminSection title="Speicher nach Kategorie" testId="admin-dash-categories">
            <ul className="lfx-admin-list">
              {STORAGE_CATEGORIES.map((c) => (
                <li key={c.key} className="lfx-admin-cat" data-testid={`admin-dash-cat-${c.key}`}>
                  <span className="lfx-admin-cat__label">{c.label}</span>
                  <ShareBar bytes={s.storage[c.key]} total={s.storage.totalBytes} testId={`admin-dash-cat-share-${c.key}`} label={c.label} />
                  <span className="lfx-admin-cat__size">{formatBytes(s.storage[c.key])}</span>
                </li>
              ))}
            </ul>
          </AdminSection>
          <AdminSection
            title="Discord-Brücke"
            testId="admin-dash-bridge"
            actions={
              <Link to="bridge" className="lfx-link" data-testid="admin-dash-bridge-link">
                Zur Brücke
              </Link>
            }
          >
            <div className="lfx-row">
              {bridgeState && (
                <Badge tone={bridgeState.tone} testId="admin-dash-bridge-state">
                  {bridgeState.label}
                </Badge>
              )}
              <span className="lfx-muted">
                {s.bridge.botUser ? `Bot: ${s.bridge.botUser.username}` : 'Kein Bot verbunden'} · {s.bridge.guilds.length} gespiegelte Server · Stand{' '}
                {formatDateTime(s.bridge.updatedAt)}
              </span>
            </div>
            {s.bridge.lastError && (
              <div className="lfx-error" data-testid="admin-dash-bridge-error">
                {s.bridge.lastError}
              </div>
            )}
          </AdminSection>
        </>
      )}
      {storage.isLoading && <Loading testId="admin-dash-storage-loading" />}
      <AdminError error={storage.error} testId="admin-dash-storage-error" />
      {storage.data && <StorageDetails storage={storage.data} />}
    </div>
  );
}
